import { useMemo, useState } from "react";
import { Button, Input, Pill } from "@guestpilot/ui-kit";
import type { Team } from "../api/types";
import { formatDate } from "../format";
import { useI18n } from "../i18n";
import { useSession } from "../session";

/**
 * The Teams of a new campaign (personal-app.md): one row per Team with what it holds, a search once the list is long, select all, and
 * the Teams that are already in an open campaign set apart at the bottom, not selectable, each with a link to that campaign.
 */
export function CampaignTeamPicker({
  teams,
  picked,
  onChange,
  onOpenCampaign,
}: {
  teams: Team[];
  picked: Set<string>;
  onChange: (next: Set<string>) => void;
  onOpenCampaign: (campaignId: string) => void;
}) {
  const { t, locale } = useI18n();
  const tz = useSession().me.tenant.timeZone;
  const [query, setQuery] = useState("");
  const { free, busy } = useMemo(() => {
    const sorted = [...teams].sort((a, b) => a.displayName.localeCompare(b.displayName));
    return {
      free: sorted.filter((x) => !x.activeCampaignId),
      busy: sorted.filter((x) => x.activeCampaignId),
    };
  }, [teams]);
  const match = (x: Team) => x.displayName.toLowerCase().includes(query.trim().toLowerCase());
  const shown = free.filter(match);
  const shownBusy = busy.filter(match);
  const allShownPicked = shown.length > 0 && shown.every((x) => picked.has(x.id));
  const toggle = (id: string, on: boolean) => {
    const n = new Set(picked);
    if (on) n.add(id);
    else n.delete(id);
    onChange(n);
  };

  return (
    <fieldset className="gp-pick">
      <legend className="t-body-strong">{t("ui:launch.teams")}</legend>
      <div className="gp-pick-bar">
        {teams.length > 6 ? (
          <Input
            type="search"
            aria-label={t("ui:launch.search")}
            placeholder={t("ui:launch.search")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        ) : (
          <span className="t-body-sm gp-muted">
            {t("ui:launch.selected", { n: picked.size, total: free.length })}
          </span>
        )}
        <Button
          variant="ghost"
          disabled={shown.length === 0}
          onClick={() => {
            const n = new Set(picked);
            for (const x of shown) {
              if (allShownPicked) n.delete(x.id);
              else n.add(x.id);
            }
            onChange(n);
          }}
        >
          {allShownPicked ? t("ui:launch.clear") : t("ui:launch.selectAll")}
        </Button>
      </div>
      {teams.length > 6 ? (
        <span className="t-body-sm gp-muted" role="status">
          {t("ui:launch.selected", { n: picked.size, total: free.length })}
        </span>
      ) : null}

      <ul className="gp-pick-list" aria-label={t("ui:launch.teams")}>
        {shown.map((x) => (
          <li key={x.id}>
            <label className="gp-pick-row" data-selected={picked.has(x.id) ? "true" : "false"}>
              <input
                type="checkbox"
                checked={picked.has(x.id)}
                onChange={(e) => toggle(x.id, e.target.checked)}
              />
              <span className="gp-pick-text">
                <span className="t-body-strong">{x.displayName}</span>
                <span className="t-body-sm gp-muted">
                  {t("ui:launch.users", { n: x.externalUserCount ?? 0 })}
                </span>
              </span>
              {x.reviewStatus === "completed" ? (
                <Pill variant="success">
                  {x.lastReviewedAt
                    ? t("ui:launch.reviewedOn", { date: formatDate(x.lastReviewedAt, locale, tz) })
                    : t("ui:launch.reviewed")}
                </Pill>
              ) : x.reviewStatus === "not_started" ? (
                <Pill>{t("ui:launch.neverReviewed")}</Pill>
              ) : null}
            </label>
          </li>
        ))}
        {shown.length === 0 && shownBusy.length === 0 ? (
          <li className="t-body gp-muted gp-pick-empty">{t("ui:launch.noMatch")}</li>
        ) : null}
      </ul>

      {shownBusy.length > 0 ? (
        <>
          <h3 className="t-body-sm gp-muted gp-pick-sub">{t("ui:launch.busyTitle")}</h3>
          <ul className="gp-pick-list" aria-label={t("ui:launch.busyTitle")}>
            {shownBusy.map((x) => (
              <li key={x.id} className="gp-pick-row" data-disabled="true">
                <span className="gp-pick-text">
                  <span className="t-body-strong">{x.displayName}</span>
                  <span className="t-body-sm gp-muted">{t("ui:launch.busyBody")}</span>
                </span>
                <Button variant="ghost" onClick={() => onOpenCampaign(x.activeCampaignId ?? "")}>
                  {t("ui:launch.viewCampaign")}
                </Button>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </fieldset>
  );
}
